import { defineStore } from "pinia";
import { doc, query, collection, updateDoc, addDoc, setDoc } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { db, converter } from "@/firebase";
import { getEnvCollection } from "@/firebase/collections";
import { useFirestore } from "@vueuse/firebase";
import { useAdminStore } from "./admin";
import { computed } from "vue";
import { Environment, Operator } from "@/models";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

const toast = useToast();
const functions = getFunctions();

export const useOperatorStore = defineStore("operators", () => {
  const adminStore = useAdminStore();
  const userEnv = computed(() => adminStore.user?.environment);

  const operatorsQuery = computed(
    () =>
      !!userEnv.value &&
      query(
        collection(db, getEnvCollection(userEnv.value, 'OPERATORS'))
      ).withConverter(converter<Operator>())
  );
  const operators = useFirestore<Operator>(operatorsQuery);

  async function saveOperator(operator: Operator): Promise<void> {
    try {
      const operatorRef = doc(
        db,
        getEnvCollection(userEnv.value!, 'OPERATORS'),
        operator.id
      );
      return await updateDoc(operatorRef, {
        ...operator,
        updatedAt: new Date(),
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }
  async function resetPassword(
    operator: Operator,
    password: string
  ): Promise<void> {
    try {
      // password too short...
      if (password.trim().length < 5) return;
      await saveOperator({
        ...operator,
        plainPassword: password,
      });
      toast.success(
        `Password reset for user ${operator.firstName} ${operator.lastName}`
      );
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`An error occured while resetting password: ${error}`);
    }
  }
  async function createOperator(operator: Partial<Operator>): Promise<void> {
    try {
      if (!(operator.firstName && operator.lastName)) {
        toast.error('First name and last name are required');
        return;
      }

      try {
        const createOperatorFunc = httpsCallable(functions, 'admin-createOperator');
        const result = await createOperatorFunc({
          environmentId: userEnv.value,
          username: operator.username,
          email: operator.email,
          password: operator.password,
          role: operator.role || 'operator',
          firstName: operator.firstName,
          lastName: operator.lastName,
        });

        const response = result.data as { success: boolean; message: string };
        
        if (response.success) {
          toast.success(`Added operator ${operator.firstName} ${operator.lastName}`);
          return;
        } else {
          throw new Error(response.message);
        }
      }  catch (functionError) {
        console.warn(
          'New operator creation function not available, falling back to direct Firestore:',
          functionError
        );
      
        const formattedOperator: Operator = {
          ...(operator as Operator),
          plainPassword: operator.password || '',
        };
      
        const docRef = doc(collection(db, getEnvCollection(userEnv.value!, 'OPERATORS')));
      
        await setDoc(docRef, {
          ...formattedOperator,
          id: docRef.id,
          updatedAt: new Date(),
          createdAt: new Date(),
        });
      
        toast.success(`Added operator ${operator.firstName} ${operator.lastName}`);
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error(`An error occurred while adding operator: ${error}`);
    }
  }

  async function generateOneTimeCode(operatorId: string): Promise<{ success: boolean; code?: string; message: string }> {
    try {
      const generateCodeFunc = httpsCallable(functions, 'admin-generateOneTimeCode');
      const result = await generateCodeFunc({
        environmentId: userEnv.value,
        operatorId,
      });

      const response = result.data as { success: boolean; code?: string; message: string };
      
      if (response.success) {
        toast.success('One-time code generated successfully');
        return response;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = `Failed to generate one-time code: ${error}`;
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    }
  }

  async function authenticateWithCode(code: string, environmentId: string): Promise<{ success: boolean; token?: string; message: string }> {
    try {
      const authWithCodeFunc = httpsCallable(functions, 'admin-authWithOneTimeCode');
      const result = await authWithCodeFunc({
        code,
        environmentId,
      });

      const response = result.data as { success: boolean; token?: string; message: string };
      return response;
    } catch (error) {
      Sentry.captureException(error);
      const errorMessage = `Failed to authenticate with code: ${error}`;
      return { success: false, message: errorMessage };
    }
  }

  const clear = () => {
    operators.value = [];
  }

  return {
    operators,
    clear,
    saveOperator,
    createOperator,
    resetPassword,
    generateOneTimeCode,
    authenticateWithCode,
  };
});

export interface User {
  id: string;
  environment: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  username: string;
}
